import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { User, Patient, Alert, Facility } from '../types';
import { getPatients, getAlerts } from '../services/api';
import { Link } from 'react-router-dom';

interface DashboardProps {
  user: User;
  onLogout: () => void;
  facilities?: Facility[];
  selectedFacility?: string;
  onFacilityChange?: (facilityId: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ user, onLogout, facilities, selectedFacility, onFacilityChange }) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [selectedFacility]);

  const loadData = async () => {
    try {
      const [patientsData, alertsData] = await Promise.all([
        getPatients(),
        getAlerts('active'),
      ]);

      // Filter by selected facility if applicable
      const selectedFacilityName = facilities?.find(f => f.id === selectedFacility)?.name;

      const filteredPatients = selectedFacilityName
        ? patientsData.filter((p: Patient) => p.facility_name === selectedFacilityName)
        : patientsData;

      const filteredAlerts = selectedFacilityName
        ? alertsData.filter((a: Alert) => a.facility_name === selectedFacilityName)
        : alertsData;

      setPatients(filteredPatients);
      setAlerts(filteredAlerts);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { name: 'Total Patients', value: patients.length },
    { name: 'Active Alerts', value: alerts.filter(a => a.status === 'active').length },
    { name: 'Critical Alerts', value: alerts.filter(a => a.severity === 'critical').length },
    { name: 'Active Devices', value: patients.reduce((sum, p) => sum + (p.active_devices || 0), 0) },
  ];

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} facilities={facilities} selectedFacility={selectedFacility} onFacilityChange={onFacilityChange}>
        <div className="text-center">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} facilities={facilities} selectedFacility={selectedFacility} onFacilityChange={onFacilityChange}>
      <div className="px-4 sm:px-0">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Dashboard</h1>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {stats.map((stat) => (
            <div key={stat.name} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">{stat.name}</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{stat.value}</dd>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Alerts */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Alerts</h2>
            {alerts.length === 0 ? (
              <p className="text-gray-500">No active alerts</p>
            ) : (
              <div className="space-y-3">
                {alerts.slice(0, 5).map((alert) => (
                  <div
                    key={alert.id}
                    className={`p-3 rounded border-l-4 ${
                      alert.severity === 'critical'
                        ? 'bg-red-50 border-red-500'
                        : alert.severity === 'high'
                        ? 'bg-orange-50 border-orange-500'
                        : 'bg-yellow-50 border-yellow-500'
                    }`}
                  >
                    <div className="flex justify-between">
                      <div>
                        <div className="font-medium">{alert.title}</div>
                        <div className="text-sm text-gray-600">{alert.patient_name} - Room {alert.room_number}</div>
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(alert.created_at).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Link to="/alerts" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
              View all alerts →
            </Link>
          </div>
        </div>

        {/* Patients */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Patients</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {patients.slice(0, 6).map((patient) => (
                <Link
                  key={patient.id}
                  to={`/patients/${patient.id}`}
                  className="block p-4 border rounded-lg hover:shadow-md transition"
                >
                  <div className="font-medium">{patient.first_name} {patient.last_name}</div>
                  <div className="text-sm text-gray-600">Room {patient.room_number}</div>
                  {patient.active_alerts > 0 && (
                    <div className="mt-2 text-xs text-red-600 font-medium">
                      {patient.active_alerts} active alert{patient.active_alerts > 1 ? 's' : ''}
                    </div>
                  )}
                </Link>
              ))}
            </div>
            <Link to="/patients" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
              View all patients →
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
